/**
 * Connection frame — Composio v3.
 *
 * Lightweight canvas hub showing the user's active Composio
 * connections. Mostly informational — disconnects happen in the
 * Settings → Integrations tab, which has confirmation dialogs and a
 * full search experience. The frame surface exists so agents can drop
 * a "your connected apps" card onto the canvas in response to a
 * "what's connected?" prompt.
 */
import { useEffect } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesConnectionMessages } from "@/i18n/messages/frames-connection";
import { cn } from "@/lib/cn";
import { connectionStatusLabel, findToolkit } from "@/lib/composio";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useIntegrationStore } from "@/stores/integration-store";
import type { IntegrationConnection } from "@/api/integrations";

export function ConnectionFrame({ id, selected }: FrameProps) {
  const t = useT(framesConnectionMessages);
  const frameSurface = useFrameSurface(id);
  const connections = useIntegrationStore((s) => s.connections);
  const toolkits = useIntegrationStore((s) =>
    Array.isArray(s.toolkits) ? s.toolkits : [],
  );
  const loadToolkits = useIntegrationStore((s) => s.actions.loadToolkits);
  const refreshConnections = useIntegrationStore(
    (s) => s.actions.refreshConnections,
  );

  useEffect(() => {
    if (toolkits.length === 0) void loadToolkits();
    void refreshConnections();
  }, [toolkits.length, loadToolkits, refreshConnections]);

  const active: IntegrationConnection[] = Array.from(connections.values())
    .filter((c) => c.status === "active")
    .sort((a, b) => a.toolkit_slug.localeCompare(b.toolkit_slug));

  return (
    <div
      className={cn(
        frameSurface.className,
        "flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="connection-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none flex min-h-0 flex-1 flex-col gap-2 px-4 py-4",
          selected && "nowheel",
        )}
        data-frame-content="true"
        data-testid="connection-frame-body"
      >
        {active.length === 0 ? (
          <div
            className="flex flex-1 flex-col items-center justify-center gap-2 text-center"
            data-testid="connection-frame-empty"
          >
            <Icons.Plug className="h-6 w-6 text-muted" />
            <p className="text-canvas-base text-secondary">
              {t("noConnections")}
            </p>
            <p className="text-canvas-sm text-muted">{t("connectHint")}</p>
          </div>
        ) : (
          <ul
            className="flex flex-col gap-2"
            data-testid="connection-frame-list"
          >
            {active.map((c) => (
              <ConnectionRow
                key={c.id}
                connection={c}
                iconUrl={findToolkit(c.toolkit_slug, toolkits)?.icon_url}
                displayName={
                  findToolkit(c.toolkit_slug, toolkits)?.display_name ??
                  c.toolkit_slug
                }
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------- Row ---------- */

interface ConnectionRowProps {
  connection: IntegrationConnection;
  iconUrl?: string;
  displayName: string;
}

function ConnectionRow({
  connection,
  iconUrl,
  displayName,
}: ConnectionRowProps) {
  return (
    <li
      className="flex items-center gap-2"
      data-testid={`connection-frame-item-${connection.toolkit_slug}`}
    >
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          className="h-7 w-7 shrink-0 rounded-control-sm bg-btn-ghost-fill object-contain p-1"
          loading="lazy"
        />
      ) : (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control-sm bg-btn-ghost-fill text-secondary">
          <Icons.Plug className="h-3.5 w-3.5" />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-canvas-sm font-medium text-primary">
          {displayName}
        </span>
        <span className="text-canvas-2xs text-secondary">
          {connectionStatusLabel(connection.status)}
        </span>
      </div>
    </li>
  );
}
